import { FilterService } from 'src/app/filter/filter.service';
import { ElementResultInfo, OfflineInspectionStatus } from "projects/phdsoft/common/src/lib/models/element-result-info.model";
import { ELEMENTSTYPESIDS } from "projects/phdsoft/common/src/lib/shared/constants/elementsTypesIds";
import { ChecklistItem } from "src/app/collections/collection-inspection/_models/checklist.model";
import { StructuralElement } from "../StructureElements/StructuralElement";
import { WorkableElement } from "../WorkableElement";
import type { Mesh, Material } from '@phdsoft/three';
import type { InspectionPointInstanceManager } from 'src/app/digital-twin/graphics/inspection-point-instance-manager.service';

export class GaugingElement extends WorkableElement {

    /**
     * Service locator for the InstancedMesh manager.
     *
     * Set once during app initialization (before the first GaugingElement is processed)
     * by the loadInspectionElements integration code in asset-manager-downloaded-inspection.ts.
     *
     * Type-only import is used to prevent the circular dependency:
     *   gaugingElement -> inspection-point-instance-manager -> gaugingElement
     *
     * VIS-04 / VIS-06
     */
    static instanceManager: InspectionPointInstanceManager | null = null;

    public VerifyProperties() {

        throw new Error("Method not implemented.");
    }

    protected _estimatedValue: number;
    public get estimatedValue(): number {
        return this._estimatedValue;
    }
    public set estimatedValue(v: number) {
        this._estimatedValue = v;
    }

    protected _gaugingValue: number;
    public get gaugingValue(): number {
        return this._gaugingValue;
    }
    public set gaugingValue(v: number) {
        this._gaugingValue = v;
    }

    private _saved: boolean;
    public get saved(): boolean {
        return this._saved;
    }
    public set saved(v: boolean) {
        this._saved = v;
    }

    public originalMaterial: Material | Material[];

    private _active: boolean = true;
    public get active(): boolean {
        return this.elementResult.Active ;
    }
    public set active(v: boolean) {
        this._active = v;
        // if (this._active)
        this.elementResult.Active = v;
        // VIS-04: PhantomSurfaceDesign.visible setter forwards to
        // _manager.setInstanceVisible — one call is correct and sufficient.
        // A redundant direct manager call here was removed because the second
        // setInstanceVisible(false) would save the zero matrix over the real
        // saved matrix, permanently preventing restore on setInstanceVisible(true).
        this.SurfaceDesign.visible = this._active;
    }

    /**
     * VIS-06: Override realVisibility to query the InstancedMesh manager state
     * instead of traversing the phantom SurfaceDesign's null parent chain.
     *
     * The base class GeometricElement.realVisibility traverses
     * `SurfaceDesign.parent.visible` — with a PhantomSurfaceDesign, parent is null,
     * causing a null-pointer exception. This override prevents that traversal.
     */
    public override get realVisibility(): boolean {
        if (GaugingElement.instanceManager) {
            return GaugingElement.instanceManager.getInstanceVisible(this);
        }
        // Fallback for non-instanced elements (no manager set).
        // Read the phantom's _visible mirror directly via the .visible getter.
        return this.SurfaceDesign?.visible ?? false;
    }

    private _isHighlightedByFilter: boolean = false;
    public get isHighlightedByFilter(): boolean {
        return this._isHighlightedByFilter;
    }
    public set isHighlightedByFilter(v: boolean) {
        this._isHighlightedByFilter = v;
    }

    public isOrphan: boolean = false;

    constructor(mesh: Mesh, material: Material, targetElements: WorkableElement[], checkList: Array<ChecklistItem>, _elementResult: ElementResultInfo) {
        super(mesh, material, _elementResult.id);
        this.label = _elementResult.Label;

        // Handle orphan inspections (no target)
        if (targetElements && targetElements.length > 0) {
            this._targetElement = targetElements[0];
            this.targetElements = targetElements;
            this.regionId = targetElements[0].regionId;
            this.regionIds = targetElements[0].regionIds;
            this.regionName = targetElements[0].regionName;
            this.SectionId = targetElements[0].SectionId;
        } else {
            this._targetElement = null;
            this.targetElements = [];
            this.isOrphan = true;
            // regionId, regionIds, regionName, SectionId will be set by caller
        }

        this.targetLabels = _elementResult.TargetLabels;
        this.Guid = _elementResult.id;
        this.elementTypeName = 'GaugingElement';
        this.scope = _elementResult.Scope;
        this._checkList = checkList ?? new Array<ChecklistItem>();
        this._elementResult = _elementResult;
        this._elementResult.Element = this;
        this.isOfflineInspection = _elementResult.IsOfflineInspection;
        this.offlineInspectionStatus = _elementResult.OfflineInspectionStatus;
        this.TypeId = ELEMENTSTYPESIDS.INSPECTION;
    }

    private _elementResult: ElementResultInfo;
    public get elementResult(): ElementResultInfo {
        return this._elementResult;
    }
    public set elementResult(v: ElementResultInfo) {
        this._elementResult = v;
        if (v != null && v.Element == null) {
            this._elementResult.Element = this;
        }
    }

    private _scope: string;
    public get scope(): string {
        return this._scope;
    }
    public set scope(v: string) {
        this._scope = v;
    }

    public get inspectionTypeId(): string {
        return this._elementResult.InspectionTypeId;
    }

    public get inspectionMethodId(): string {
        return this._elementResult.InspectionMethodId;
    }

    private _targetElement: WorkableElement;
    public get targetElement(): WorkableElement {
        return this._targetElement;
    }

    /**
     * Points this inspection at a new target element and copies the target's
     * region/section identity. Domain fields only: elementResult, the mesh and the
     * targets' gaugingElements lists are left to the caller.
     */
    public retarget(newTarget: WorkableElement): void {
        this._targetElement = newTarget;
        this.targetElements = [newTarget];
        this.regionId = newTarget.regionId;
        this.regionIds = newTarget.regionIds?.length ? newTarget.regionIds : [newTarget.regionId];
        this.regionName = newTarget.regionName;
        this.SectionId = newTarget.SectionId;
        this.SectionName = newTarget.SectionName;
        this.isOrphan = false;
        this.targetLabels = newTarget.properties?.General?.Label || newTarget.Guid;
    }

    // private _targetElements: WorkableElement[];
    public targetElements: WorkableElement[]
    // {
    //     return this.targetElements;
    // }

    public targetLabels: string;
    // private _targetLabels: WorkableElement[];
    // public get targetLabels(): WorkableElement[] {
    //     return this._targetLabels;
    // }

    public isOfflineInspection: boolean;
    public offlineInspectionStatus: OfflineInspectionStatus

    public get targetElementId(): string {
        return this._targetElement?.Guid ?? null;
    }

    public get targetElementType(): ELEMENTSTYPESIDS {
        return this._targetElement?.TypeId ?? null;
    }

    private _checkList: Array<ChecklistItem> = new Array<ChecklistItem>();
    public get checkList(): Array<ChecklistItem> {
        return this._checkList;
    }
    public set checkList(v: Array<ChecklistItem>) {
        this._checkList = v;
    }

    // private _checklist: Array<ChecklistItem> = new Array<ChecklistItem>();;
    public get checklist(): Array<ChecklistItem> {
        return this._checkList;
    }
    public set checklist(v: Array<ChecklistItem>) {
        this._checkList = v;
    }

    public updateMaterial(data: Date, newMaterial): void {
        if (newMaterial) this.defaultMaterial = newMaterial
    }

}
